import { useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { celebratesOutcome, launchConfetti } from "../../celebrate/confetti";
import { useGavionConfig } from "../../config/useGavionConfig";
import {
  DIAL_OUTCOMES,
  validateCallbackDay,
} from "../../domain/dialer/outcomes";
import type { DialOutcome } from "../../domain/dialer/types";
import { useDialerApi } from "../api/DialerApiContext";
import { DialerApiError, type CallRow } from "../api/types";
import type { FieldError } from "../screen/dialerReducer";
import { OutcomeForm } from "../screen/OutcomeForm";
import {
  consentEmail,
  emptyOutcomeForm,
  formToPayloadExtras,
} from "../screen/outcomeFormModel";
import { useRecordConsent } from "../screen/useRecordConsent";

type Props = {
  call: CallRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
  leadTimezone?: string;
  leadEmail?: string | null;
  now?: () => Date;
  /** GAVION inbound calls: the results offered (default: every dialer result). */
  outcomes?: readonly DialOutcome[];
  /** GAVION inbound calls: the dialog's title (default « Compléter l'appel »). */
  title?: string;
  /** GAVION inbound calls: « Arrête ta note Granola » under the title. */
  granolaReminder?: boolean;
};

const initialOutcome = (call: CallRow): DialOutcome | null =>
  call.outcome && call.outcome !== "unlogged" && call.outcome !== "skipped"
    ? call.outcome
    : null;

/** Sets or edits a call outcome after the fact; the server applies the same validation and automation as the dialer. */
export const CallOutcomeDialog = ({
  call,
  open,
  onOpenChange,
  onDone,
  leadTimezone: tzProp,
  leadEmail: emailProp,
  now = () => new Date(),
  outcomes,
  title,
  granolaReminder = false,
}: Props) => {
  const t = useTranslate();
  // The lead's own zone and email come with the row; callbacks and demos convert in that zone.
  const leadTimezone = tzProp ?? call.company_timezone ?? "America/Toronto";
  const leadEmail = emailProp ?? call.company_email ?? null;
  const api = useDialerApi();
  const recordConsent = useRecordConsent();
  const { config } = useGavionConfig();
  const [outcome, setOutcome] = useState<DialOutcome | null>(
    initialOutcome(call),
  );
  // GAVION demo-contact: an edit of a booked demo starts with its attendee's address, so saving it without retyping
  // keeps the attendee's contact (SQL log_call_outcome 68_ keeps it too when no address is sent)
  const [form, setForm] = useState(() => ({
    ...emptyOutcomeForm(),
    attendee_email:
      typeof call.demo?.attendee_email === "string"
        ? call.demo.attendee_email
        : "",
  }));
  const [error, setError] = useState<FieldError | null>(null);
  const [saving, setSaving] = useState(false);
  const [offline, setOffline] = useState(false);
  const known = (code: string) =>
    t(`gavion.dialer.errors.${code}`, { _: "__missing__" }) !== "__missing__"
      ? code
      : "unknown";

  const save = async () => {
    if (!outcome || saving) return;
    setSaving(true);
    setError(null);
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      setOffline(true);
      setSaving(false);
      return;
    }
    setOffline(false);
    const payload = {
      call_id: call.id,
      version: (call.outcome_version ?? 0) + 1,
      outcome,
      ...formToPayloadExtras(form, outcome, leadTimezone),
    };
    // GAVION missed-call: no callback on a weekend / Dec 25 / Jan 1 (suggestion: next business day, same time)
    const day = validateCallbackDay(payload, leadTimezone);
    if (!day.ok) {
      setError({ code: day.code, suggestion: day.suggestion });
      setSaving(false);
      return;
    }
    try {
      await api.logOutcome(payload);
    } catch (e) {
      setError({
        code: known(e instanceof DialerApiError ? e.code : "unknown"),
        suggestion: e instanceof DialerApiError ? e.detail : undefined,
      });
      setSaving(false);
      return;
    }
    // GAVION celebrations: a newly booked demo, not a correction of one already booked
    if (celebratesOutcome(outcome) && call.outcome !== outcome)
      launchConfetti();
    const email = consentEmail(form, outcome, leadEmail);
    if (email && call.company_id !== null)
      await recordConsent({
        email,
        companyId: call.company_id,
        callId: call.id,
        obtainedAt: now().toISOString(),
        offline: false,
      });
    setSaving(false);
    onDone();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {title ?? t("gavion.dialer.todo.dialog_title")}
          </DialogTitle>
        </DialogHeader>
        {granolaReminder && (
          <p className="text-sm text-muted-foreground">
            {t("gavion.dialer.granola.stop_reminder")}
          </p>
        )}
        <Label id="co-outcome">{t("gavion.dialer.todo.outcome")}</Label>
        <RadioGroup
          aria-labelledby="co-outcome"
          value={outcome ?? ""}
          onValueChange={(v) => {
            setOutcome(v as DialOutcome);
            setError(null);
          }}
          className="grid grid-cols-1 gap-2 sm:grid-cols-2"
        >
          {DIAL_OUTCOMES.filter(
            (o) => !outcomes || outcomes.includes(o.code),
          ).map((o) => (
            <div key={o.code} className="flex items-center gap-2">
              <RadioGroupItem id={`co-${o.code}`} value={o.code} />
              <Label htmlFor={`co-${o.code}`}>
                {t(`gavion.dialer.outcomes.${o.code}`)}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {outcome && (
          <OutcomeForm
            outcome={outcome}
            form={form}
            setForm={setForm}
            error={error}
            leadEmail={leadEmail}
            leadTimezone={leadTimezone}
            now={now}
            callbackTime={config.callback_default_time}
          />
        )}
        {offline && (
          <p role="alert" className="text-sm font-medium text-red-700">
            {t("gavion.dialer.todo.offline")}
          </p>
        )}
        <DialogFooter>
          <Button disabled={!outcome || saving} onClick={() => void save()}>
            {t("gavion.dialer.form.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
